/**
 * People-by-name profile link — the "safety net" citation.
 *
 * When the model NAMES a councillor in its answer but forgets the `[N]`
 * citation marker, the backend still ships that councillor's {name, url} on a
 * side-channel (`linkable-people` SSE event — the URL never entered the model
 * context). The markdown renderer matches the full name in the answer text and
 * wraps it in this component, so the reader still gets a click-through to the
 * profile.
 *
 * Visually consistent with the numbered `CitationPill`: same accent styling and
 * the same floating hover card (type + host), but the pill content is the
 * councillor's NAME (rendered inline in the prose) rather than a `[N]` number.
 */
import { useState } from 'react';
import { User } from 'lucide-react';
import {
  useFloating,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  flip,
  shift,
  offset,
  autoUpdate,
  FloatingPortal,
} from '@floating-ui/react';
import { safeNavUrl } from '../lib/safeUrl';
import { useT } from '../i18n';

interface PeoplePillProps {
  /** The matched full name, verbatim from the rendered answer text. */
  name: string;
  /** Councillor profile URL (from the backend side-channel, opened on click). */
  url: string;
  /** Portrait image URL, or null/absent → render the name link with no avatar. */
  portrait?: string | null;
}

function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

export function PeoplePill({ name, url, portrait }: PeoplePillProps) {
  const [open, setOpen] = useState(false);
  const t = useT();

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: 'top',
    middleware: [offset(6), flip({ padding: 8 }), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  const hover   = useHover(context, { delay: { open: 0, close: 100 } });
  const focus   = useFocus(context);
  const dismiss = useDismiss(context);
  const role    = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Protocol-check the profile URL, same as CitationPill (Audit H2).
    const safe = safeNavUrl(url);
    if (safe) window.open(safe, '_blank', 'noopener,noreferrer');
  };

  const typeLabel = t('citation.type.councillor');
  const host      = hostnameOf(url);

  return (
    <>
      <button
        ref={refs.setReference}
        {...getReferenceProps({ onClick: handleClick })}
        type="button"
        className="
          inline-flex items-center gap-[3px] align-middle mx-[1px]
          px-[7px] py-[1px] rounded-full no-underline
          text-[11px] font-medium leading-[1.5]
          bg-[var(--accent)]/10 text-[var(--accent)] border border-[var(--accent)]/25
          hover:bg-[var(--accent)] hover:text-white hover:border-[var(--accent)]
          transition-colors
        "
        style={{ transform: 'translateY(-0.5px)' }}
        aria-label={t('citation.aria').replace('{id}', name).replace('{label}', typeLabel)}
      >
        {portrait ? (
          <img
            src={portrait}
            alt=""
            aria-hidden="true"
            loading="lazy"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
            className="w-[14px] h-[14px] rounded-full object-cover object-top shrink-0 bg-[var(--surface)]"
          />
        ) : (
          <User size={10} className="shrink-0" />
        )}
        {name}
      </button>

      {open && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            {...getFloatingProps()}
            style={{
              ...floatingStyles,
              width: 240,
              maxWidth: 'calc(100vw - 16px)',
              zIndex: 10000,
            }}
            className="
              pointer-events-none
              px-2.5 py-1.5 rounded-[var(--r2)]
              bg-[var(--text-1)] text-white
              shadow-lg
              text-[11px] font-sans leading-snug
              break-words
            "
          >
            <span className="block text-[11px] font-mono uppercase tracking-wider opacity-70 mb-0.5">
              {typeLabel}
            </span>
            <span className="block font-medium">{name}</span>
            {host && (
              <span className="block text-[9.5px] font-mono opacity-60 mt-0.5 truncate">
                {host}
              </span>
            )}
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
